import type {
  EventGQL,
  Banner,
  TeamStanding,
  ShopProduct,
  FeedItem,
  TeamStats,
  TeamGQL,
  Coach,
} from '../../Api'
import { hasChampSeriesStarted } from '../../Config/League/league.config'

// Action type constants
export const TEAM_ACTIONS = {
  SET_TEAM_DATA: 'SET_TEAM_DATA',
  SET_TEAM_LOADING: 'SET_TEAM_LOADING',
  SET_TEAM_STATS: 'SET_TEAM_STATS',
  SET_TEAM_CS_STATS: 'SET_TEAM_CS_STATS',
  SET_EVENTS: 'SET_EVENTS',
  SET_EVENTS_LOADING: 'SET_EVENTS_LOADING',
  SET_TEAM_FEED: 'SET_TEAM_FEED',
  SET_TEAM_FEED_LOADING: 'SET_TEAM_FEED_LOADING',
  SET_COACHES: 'SET_COACHES',
  SET_BANNERS: 'SET_BANNERS',
  SET_STANDINGS: 'SET_STANDINGS',
  SET_MERCH_COLLECTION: 'SET_MERCH_COLLECTION',
  SET_CS_STARTED: 'SET_CS_STARTED',
  SET_ERROR: 'SET_ERROR',
} as const

export type TeamState = {
  team: TeamGQL | null
  teamLoading: boolean
  stats: TeamStats | null
  csStats: TeamStats | null
  events: EventGQL[]
  eventsLoading: boolean
  teamFeed: FeedItem[]
  feedLoading: boolean
  coaches: Coach[]
  banners: Banner[]
  standings: TeamStanding[]
  teamMerchCollection: ShopProduct[]
  csStarted: boolean
  error: Error | null
}

export type TeamAction =
  | { type: typeof TEAM_ACTIONS.SET_TEAM_DATA; payload: TeamGQL }
  | { type: typeof TEAM_ACTIONS.SET_TEAM_LOADING; payload: boolean }
  | { type: typeof TEAM_ACTIONS.SET_TEAM_STATS; payload: TeamStats | null }
  | { type: typeof TEAM_ACTIONS.SET_TEAM_CS_STATS; payload: TeamStats | null }
  | { type: typeof TEAM_ACTIONS.SET_EVENTS; payload: EventGQL[] }
  | { type: typeof TEAM_ACTIONS.SET_EVENTS_LOADING; payload: boolean }
  | { type: typeof TEAM_ACTIONS.SET_TEAM_FEED; payload: FeedItem[] }
  | { type: typeof TEAM_ACTIONS.SET_TEAM_FEED_LOADING; payload: boolean }
  | { type: typeof TEAM_ACTIONS.SET_COACHES; payload: Coach[] }
  | { type: typeof TEAM_ACTIONS.SET_BANNERS; payload: Banner[] }
  | { type: typeof TEAM_ACTIONS.SET_STANDINGS; payload: TeamStanding[] }
  | { type: typeof TEAM_ACTIONS.SET_MERCH_COLLECTION; payload: ShopProduct[] }
  | { type: typeof TEAM_ACTIONS.SET_CS_STARTED; payload: boolean }
  | { type: typeof TEAM_ACTIONS.SET_ERROR; payload: Error | null }

export const initialState: TeamState = {
  team: null,
  teamLoading: true,
  stats: null,
  csStats: null,
  events: [],
  eventsLoading: true,
  teamFeed: [],
  feedLoading: true,
  coaches: [],
  banners: [],
  standings: [],
  teamMerchCollection: [],
  csStarted: hasChampSeriesStarted,
  error: null,
}

export const teamReducer = (state: TeamState, action: TeamAction): TeamState => {
  switch (action.type) {
    case TEAM_ACTIONS.SET_TEAM_DATA:
      return { ...state, team: action.payload }
    case TEAM_ACTIONS.SET_TEAM_LOADING:
      return { ...state, teamLoading: action.payload }
    case TEAM_ACTIONS.SET_TEAM_STATS:
      return { ...state, stats: action.payload }
    case TEAM_ACTIONS.SET_TEAM_CS_STATS:
      return { ...state, csStats: action.payload }
    case TEAM_ACTIONS.SET_EVENTS:
      return { ...state, events: action.payload }
    case TEAM_ACTIONS.SET_EVENTS_LOADING:
      return { ...state, eventsLoading: action.payload }
    case TEAM_ACTIONS.SET_TEAM_FEED:
      return { ...state, teamFeed: action.payload }
    case TEAM_ACTIONS.SET_TEAM_FEED_LOADING:
      return { ...state, feedLoading: action.payload }
    case TEAM_ACTIONS.SET_COACHES:
      return { ...state, coaches: action.payload }
    case TEAM_ACTIONS.SET_BANNERS:
      return { ...state, banners: action.payload }
    case TEAM_ACTIONS.SET_STANDINGS:
      return { ...state, standings: action.payload }
    case TEAM_ACTIONS.SET_MERCH_COLLECTION:
      return { ...state, teamMerchCollection: action.payload }
    case TEAM_ACTIONS.SET_CS_STARTED:
      return { ...state, csStarted: action.payload }
    case TEAM_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload }
    default:
      return state
  }
}
